// src/pages/TelusPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api'; // Import the default export
import DataTable from '../components/common/DataTable';
import PaginationControls from '../components/common/PaginationControls';
import CopyableId from '../components/common/CopyableId';
import SaveButton from '../components/common/SaveButton';
import TitleSearchModal from '../components/common/TitleSearchModal';
import AssetTypeFilterModal from '../components/common/AssetTypeFilterModal';
import { useAppContext } from '../context/AppContext';

const TelusPage = () => {
  const { t } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [vodAssets, setVodAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [titleFilter, setTitleFilter] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState('');
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [showAssetTypeModal, setShowAssetTypeModal] = useState(false);
  const itemsPerPage = 20; // Assuming 20 items per page for pagination

  const fetchVodAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getTelusVODAssets(currentPage, itemsPerPage, titleFilter || null, assetTypeFilter || null);
      setVodAssets(response.items);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err) {
      console.error('Failed to fetch Telus VOD assets:', err);
      setError(t('telus.error'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, titleFilter, assetTypeFilter, t]);

  // Effect to parse URL parameters on component mount or URL change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    const titleParam = params.get('title') || '';
    const assetTypeParam = params.get('assetType') || '';
    
    setCurrentPage(pageParam);
    setTitleFilter(titleParam);
    setAssetTypeFilter(assetTypeParam);
  }, [location.search]);

  // Function to update URL with current parameters
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (titleFilter) params.set('title', titleFilter);
    if (assetTypeFilter) params.set('assetType', assetTypeFilter);
    
    const newURL = `/telus?${params.toString()}`;
    
    // Only update if URL actually changed to avoid unnecessary history entries
    if (window.location.pathname + window.location.search !== newURL) {
      navigate(newURL, { replace: true });
    }
  }, [currentPage, titleFilter, assetTypeFilter, navigate]);

  // Effect to update URL when parameters change (but not on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!isInitialLoad) {
      updateURL();
    }
  }, [updateURL, isInitialLoad]);

  useEffect(() => {
    fetchVodAssets();
    // Mark that initial load is complete after first fetch
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [fetchVodAssets, isInitialLoad]);

  const handleAssetClick = (asset) => {
    setSelectedAsset(asset);
  };

  const handleCloseDetail = () => {
    setSelectedAsset(null);
  };

  const handleTitleSearch = (newTitleFilter) => {
    setTitleFilter(newTitleFilter);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleOpenTitleModal = () => {
    setShowTitleModal(true);
  };

  const handleCloseTitleModal = () => {
    setShowTitleModal(false);
  };

  const handleAssetTypeFilter = (newAssetTypeFilter) => {
    setAssetTypeFilter(newAssetTypeFilter);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleOpenAssetTypeModal = () => {
    setShowAssetTypeModal(true);
  };

  const handleCloseAssetTypeModal = () => {
    setShowAssetTypeModal(false);
  };

  const columns = [
    { 
      header: t('telus.columns.amsAssetId'), 
      accessor: 'AMSASSETID',
      render: (value) => (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            // Find the row data by matching the AMSASSETID
            const asset = vodAssets.find(asset => asset.AMSASSETID === value);
            if (asset) {
              handleAssetClick(asset);
            }
          }}
          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
        >
          {value}
        </button>
      )
    },
    { 
      header: (
        <div className="flex items-center">
          <span>{t('telus.columns.title')}</span>
          <button
            onClick={handleOpenTitleModal}
            className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
            title={t('telus.filter.searchByTitle')}
          >
            🔍
          </button>
          {titleFilter && (
            <span className="ml-1 text-xs text-blue-600" title={t('telus.filter.filteredBy', { filter: titleFilter })}>
              *
            </span>
          )}
        </div>
      ), 
      accessor: 'TITLE' 
    },
    { 
      header: t('telus.columns.tmsId'), 
      accessor: 'TMSID',
      render: (value) => (
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/program/${value}`)}
            className="text-blue-600 hover:text-blue-800 underline cursor-pointer mr-2"
            title={t('telus.columns.viewDetails')}
          >
            <CopyableId value={value} />
          </button>
          <SaveButton tmsId={value} buttonClassName="ml-1" />
        </div>
      )
    },
    { 
      header: (
        <div className="flex items-center">
          <span>{t('telus.columns.assetType')}</span>
          <button
            onClick={handleOpenAssetTypeModal}
            className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
            title={t('telus.filter.filterByAssetType')}
          >
            🔍
          </button>
          {assetTypeFilter && (
            <span className="ml-1 text-xs text-blue-600" title={t('telus.filter.filteredBy', { filter: assetTypeFilter })}>
              *
            </span>
          )}
        </div>
      ), 
      accessor: 'ASSETTYPE' 
    },
    { header: t('telus.columns.showType'), accessor: 'SHOWTYPE' },
    { header: t('telus.columns.releaseYear'), accessor: 'RELEASEYEAR' },
    { header: t('telus.columns.runtime'), accessor: 'RUNTIMEMINS' },
    { header: t('telus.columns.rating'), accessor: 'RATINGVALUE' },
    { header: t('telus.columns.contentType'), accessor: 'CONTENTTYPE' },
    { header: t('telus.columns.seriesName'), accessor: 'SERIESNAME' },
    { header: t('telus.columns.seasonNum'), accessor: 'SEASONNUMBER' },
    { header: t('telus.columns.episodeNum'), accessor: 'EPISODENUMBER' },
    { header: t('telus.columns.language'), accessor: 'LANGUAGE' },
    { header: t('telus.columns.provider'), accessor: 'PROVIDER' },
  ];

  // Render detailed view for selected asset
  const renderAssetDetail = () => {
    if (!selectedAsset) return null;

    // Parse CASTANDCREW JSON if it exists
    let castAndCrew = [];
    try {
      if (selectedAsset.CASTANDCREW) {
        castAndCrew = JSON.parse(selectedAsset.CASTANDCREW);
      }
    } catch (e) {
      console.error('Error parsing cast and crew:', e);
    }

    return (
      <div className="bg-white border rounded-lg shadow-lg p-6">
        {/* Header with close button */}
        <div className="flex justify-between items-center mb-6 border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-800">{t('telus.details.title')}</h2>
          <button 
            onClick={handleCloseDetail}
            className="text-red-600 hover:text-red-800 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-100"
            title={t('telus.details.close')}
          >
            ×
          </button>
        </div>

        {/* Asset details in organized sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.basicInfo')}</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">{t('telus.fields.amsAssetId')}</span> {selectedAsset.AMSASSETID}</div>
              <div><span className="font-medium">{t('telus.fields.title')}</span> {selectedAsset.TITLE}</div>
              <div><span className="font-medium">{t('telus.fields.tmsId')}</span> {selectedAsset.TMSID}</div>
              <div><span className="font-medium">{t('telus.fields.assetType')}</span> {selectedAsset.ASSETTYPE}</div>
              <div><span className="font-medium">{t('telus.fields.contentType')}</span> {selectedAsset.CONTENTTYPE}</div>
              <div><span className="font-medium">{t('telus.fields.showType')}</span> {selectedAsset.SHOWTYPE}</div>
              <div><span className="font-medium">{t('telus.fields.language')}</span> {selectedAsset.LANGUAGE}</div>
              <div><span className="font-medium">{t('telus.fields.country')}</span> {selectedAsset.COUNTRY}</div>
            </div>
          </div>

          {/* Series/Episode Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.seriesEpisodeInfo')}</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">{t('telus.fields.seriesName')}</span> {selectedAsset.SERIESNAME}</div>
              <div><span className="font-medium">{t('telus.fields.seriesId')}</span> {selectedAsset.SERIESID}</div>
              <div><span className="font-medium">{t('telus.fields.seasonNumber')}</span> {selectedAsset.SEASONNUMBER}</div>
              <div><span className="font-medium">{t('telus.fields.episodeNumber')}</span> {selectedAsset.EPISODENUMBER}</div>
              <div><span className="font-medium">{t('telus.fields.episodeTitle')}</span> {selectedAsset.EPISODETITLE}</div>
              <div><span className="font-medium">{t('telus.fields.originalAirDate')}</span> {selectedAsset.ORIGINALAIRDATE}</div>
              <div><span className="font-medium">{t('telus.fields.releaseYear')}</span> {selectedAsset.RELEASEYEAR}</div>
            </div>
          </div>

          {/* Technical Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.technicalInfo')}</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">{t('telus.fields.runtime')}</span> {selectedAsset.RUNTIMEMINS}</div>
              <div><span className="font-medium">{t('telus.fields.videoFormat')}</span> {selectedAsset.VIDEOFORMAT}</div>
              <div><span className="font-medium">{t('telus.fields.audio')}</span> {selectedAsset.AUDIO}</div>
              <div><span className="font-medium">{t('telus.fields.movieFormat')}</span> {selectedAsset.MOVIEFORMAT}</div>
              <div><span className="font-medium">{t('telus.fields.closedCaptioning')}</span> {selectedAsset.CLOSEDCAPTIONING}</div>
              <div><span className="font-medium">{t('telus.fields.descriptiveVideo')}</span> {selectedAsset.DESCRIPTIVEVIDEO}</div>
              <div><span className="font-medium">{t('telus.fields.subtitled')}</span> {selectedAsset.SUBTITLED}</div>
            </div>
          </div>

          {/* Rating and Business Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.ratingBusiness')}</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">{t('telus.fields.ratingSystem')}</span> {selectedAsset.RATINGSYSTEM}</div>
              <div><span className="font-medium">{t('telus.fields.ratingValue')}</span> {selectedAsset.RATINGVALUE}</div>
              <div><span className="font-medium">{t('telus.fields.starRating')}</span> {selectedAsset.STARRATING}</div>
              <div><span className="font-medium">{t('telus.fields.provider')}</span> {selectedAsset.PROVIDER}</div>
              <div><span className="font-medium">{t('telus.fields.studio')}</span> {selectedAsset.STUDIO}</div>
              <div><span className="font-medium">{t('telus.fields.popularity')}</span> {selectedAsset.POPULARITY}</div>
              <div><span className="font-medium">{t('telus.fields.storefront')}</span> {selectedAsset.STOREFRONT}</div>
            </div>
          </div>
        </div>

        {/* Descriptions */}
        <div className="mt-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.descriptions')}</h3>
          <div className="space-y-3">
            {selectedAsset.SHORTSYNOPSIS && (
              <div>
                <span className="font-medium text-sm">{t('telus.fields.shortSynopsis')}</span>
                <p className="text-sm text-gray-600 mt-1">{selectedAsset.SHORTSYNOPSIS}</p>
              </div>
            )}
            {selectedAsset.LONGSYNOPSIS && (
              <div>
                <span className="font-medium text-sm">{t('telus.fields.longSynopsis')}</span>
                <p className="text-sm text-gray-600 mt-1">{selectedAsset.LONGSYNOPSIS}</p>
              </div>
            )}
          </div>
        </div>

        {/* Cast and Crew */}
        {castAndCrew.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.castCrew')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {castAndCrew.map((person, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium">{person.firstName} {person.middleName} {person.lastName}</span>
                  <span className="text-gray-500 ml-2">({person.castType})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories and Genres */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {selectedAsset.CATEGORIES && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.categories')}</h3>
              <p className="text-sm text-gray-600 mt-2">{selectedAsset.CATEGORIES}</p>
            </div>
          )}
          {selectedAsset.GENRES && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.genres')}</h3>
              <p className="text-sm text-gray-600 mt-2">{selectedAsset.GENRES}</p>
            </div>
          )}
        </div>

        {/* Additional Technical Details */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('telus.details.playbackControls')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-3 text-sm">
            <div><span className="font-medium">{t('telus.playback.fastForward')}</span> {selectedAsset.FF}</div>
            <div><span className="font-medium">{t('telus.playback.pause')}</span> {selectedAsset.PAUSE}</div>
            <div><span className="font-medium">{t('telus.playback.replay')}</span> {selectedAsset.REPLAY}</div>
            <div><span className="font-medium">{t('telus.playback.rewind')}</span> {selectedAsset.REWIND}</div>
            <div><span className="font-medium">{t('telus.playback.skip')}</span> {selectedAsset.SKIP}</div>
            <div><span className="font-medium">{t('telus.playback.stop')}</span> {selectedAsset.STOP}</div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-4">{t('telus.loading')}</div>;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">{error}</div>;
  }

  return (
    <div className="max-w-none mx-auto p-4 sm:p-6 lg:p-8">
      <h1 className="text-2xl font-bold mb-4">{t('telus.title')}</h1>
      
      {/* Filter indicators */}
      {titleFilter && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              <span className="font-medium">{t('telus.filter.title')}</span> "{titleFilter}"
            </span>
            <button
              onClick={() => handleTitleSearch('')}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              {t('telus.filter.clear')}
            </button>
          </div>
        </div>
      )}
      
      {assetTypeFilter && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center justify-between">
            <span className="text-sm text-green-800">
              <span className="font-medium">{t('telus.filter.assetType')}</span> {assetTypeFilter}
            </span>
            <button
              onClick={() => handleAssetTypeFilter('')}
              className="text-green-600 hover:text-green-800 text-sm underline"
            >
              {t('telus.filter.clear')}
            </button>
          </div>
        </div>
      )}
      
      {selectedAsset ? (
        // Show detailed view for selected asset
        renderAssetDetail()
      ) : (
        // Show table view
        <>
          {loading && <div className="text-center py-4">{t('telus.loading')}</div>}
          {error && <div className="text-center py-4 text-red-500">{error}</div>}
          {!loading && !error && vodAssets.length > 0 ? (
            <>
              <DataTable columns={columns} data={vodAssets} />
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </>
          ) : !loading && !error && (
            <p>{t('telus.noAssets')}</p>
          )}
        </>
      )}
      
      {/* Title Search Modal */}
      <TitleSearchModal
        isOpen={showTitleModal}
        onClose={handleCloseTitleModal}
        onApply={handleTitleSearch}
        currentTitle={titleFilter}
      />
      
      {/* Asset Type Filter Modal */}
      <AssetTypeFilterModal
        isOpen={showAssetTypeModal}
        onClose={handleCloseAssetTypeModal}
        onApply={handleAssetTypeFilter}
        currentAssetType={assetTypeFilter}
      />
    </div>
  );
};

export default TelusPage;
